import { useState, useMemo, useEffect } from "react"
import { Secure } from "lib/authorization/secure"
import { AUTHORING_MANAGE_LEGISLATION_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { Box, Button, Divider, Menu, MenuItem, Stack } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { TruncatedTypography } from "lib/@components/truncated-typography"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam, useSearchParamsBatch } from "lib/@hooks/use-search-param"
import { Bound } from "lib/@components/binding/Bound"
import { BackButton } from "routes/schedule/tree/schedules/upDirectory"
import { TabledRepeat } from "lib/@components/tabled-repeat/tabled-repeat"
import { ResetTabledRepeat } from "event-definitions"
import { useDialog } from "lib/@hooks/useDialog"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { useAuthoringLegislationLibrary } from "./use-authoring-legislation-library"
import { LegislationLibraryRow } from "./components/legislation-library-row"
import { LegislationLibraryLoadingRow } from "./components/legislation-library-loading-row"
import { LegislationLibraryEditDialog } from "./components/legislation-library-edit-dialog"
import { createAuthoringLegislationGroup } from "./controller/create-authoring-legislation-group"
import { createAuthoringLegislation } from "./controller/create-authoring-legislation"
import { LEGISLATION_FOLDER, LEGISLATION, confirmDuplicateDestination } from "./legislation-library-helpers"
import { legislationLibraryRootPath, legislationLibraryItemPath } from "./legislation-library-navigation"

const TABLE_HEADERS = [
    { title: "Name", fieldKey: "name", sortable: true },
    { title: "Schedules", fieldKey: "scheduleCount", sortable: true, sx: { width: 130 } },
    { title: "Last Modified", fieldKey: "lastModified", sortable: true, sx: { width: 150 } },
    { title: "", fieldKey: "", sx: { width: 56 } },
]

const TABLE_SX = {
    tableLayout: "fixed",
    width: "100%",
    "& th:nth-of-type(2), & td:nth-of-type(2)": { width: 130 },
    "& th:nth-of-type(3), & td:nth-of-type(3)": { width: 150 },
    "& th:nth-of-type(4), & td:nth-of-type(4)": { width: 56 },
}

export function LegislationLibraryPage() {
    const [rawId] = useSearchParam("id", "")
    const id = rawId || null
    const [search] = useSearchParam("search", "")
    const setParams = useSearchParamsBatch()
    const [addNewAnchor, setAddNewAnchor] = useState(null)

    const openAddFolderDialog = useDialog(
        <LegislationLibraryEditDialog node={{ type: LEGISLATION_FOLDER }} isCreate />,
        {
            maxWidth: "sm",
            fullWidth: true,
        }
    )
    const openAddLegislationDialog = useDialog(<LegislationLibraryEditDialog node={{ type: LEGISLATION }} isCreate />, {
        maxWidth: "sm",
        fullWidth: true,
    })

    const library = useAuthoringLegislationLibrary()
    const { isLoading, state, getRootIds, getNode, getChildren, getPath, findInSubtree } = library

    const currentNode = id ? getNode(id) : null
    const pageTitle = currentNode?.name ?? "Legislation"
    const parentUrl = id
        ? currentNode?.parentId
            ? legislationLibraryItemPath(currentNode.parentId)
            : legislationLibraryRootPath()
        : null

    const parentCrumbs = id
        ? [
              { label: "Legislation", to: legislationLibraryRootPath() },
              ...getPath(id)
                  .slice(0, -1)
                  .map((node) => ({ label: node.name, to: legislationLibraryItemPath(node.id) })),
          ]
        : []

    const displayItems = useMemo(computeDisplayItems, [id, search, getRootIds, getNode, getChildren, findInSubtree])
    // Key off ids, not the array reference: the children array is mutated in place, so its reference never changes.
    const displayItemsKey = displayItems.map((node) => node?.id).join("|")

    useEffect(() => {
        ResetTabledRepeat.raiseOnce()
    }, [displayItemsKey, isLoading])

    return (
        <PageBreadcrumbContainer
            title={pageTitle}
            parentCrumbs={parentCrumbs}
            backButton={parentUrl ? <BackButton onClick={handleBack} /> : false}
            heading={
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="mdi:scale-balance" sx={{ width: 28, height: 28 }} />
                    <TruncatedTypography>{pageTitle}</TruncatedTypography>
                </Stack>
            }
        >
            <Stack spacing={1} sx={{ width: 1, flex: 1, minHeight: 0, mt: 3 }}>
                <Box>
                    <Secure demand={AUTHORING_MANAGE_LEGISLATION_DEMAND} fallback={<></>}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<Iconify icon="mdi:plus" />}
                            endIcon={<Iconify icon="mdi:chevron-down" />}
                            onClick={handleAddNewOpen}
                            data-cy="legislation-add-new-button"
                        >
                            Add New
                        </Button>
                        <Menu anchorEl={addNewAnchor} open={Boolean(addNewAnchor)} onClose={handleAddNewClose}>
                            <MenuItem onClick={handleAddFolder} data-cy="add-new-folder-option">
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon="mdi:folder" sx={{ color: "sfg20.medium", width: 22, height: 22 }} />
                                    <span>Folder</span>
                                </Stack>
                            </MenuItem>
                            <Divider />
                            <MenuItem
                                onClick={handleAddLegislation}
                                disabled={!id}
                                data-cy="add-new-legislation-option"
                            >
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <Iconify icon="mdi:scale-balance" sx={{ width: 22, height: 22 }} />
                                    <span>Legislation</span>
                                </Stack>
                            </MenuItem>
                        </Menu>
                    </Secure>
                </Box>

                {/* Keyed by folder so the box remounts empty when openFolder clears the search */}
                <SearchBoxUsingSearchParam
                    key={id ?? "root"}
                    placeholder="Search"
                    autoFocus={false}
                    sx={{ py: 0, width: 1 }}
                />

                <Bound state={state} library={library} openFolder={openFolder}>
                    <TabledRepeat
                        items={isLoading ? [{}] : displayItems}
                        itemPass="item"
                        itemComponent={isLoading ? <LegislationLibraryLoadingRow /> : <LegislationLibraryRow />}
                        tableHeaders={TABLE_HEADERS}
                        paged
                        alternating={false}
                        sx={TABLE_SX}
                    />
                </Bound>
            </Stack>
        </PageBreadcrumbContainer>
    )

    function handleBack() {
        openFolder(currentNode?.parentId ?? "")
    }

    function openFolder(folderId) {
        setParams({ id: folderId, search: "" }, { push: true })
    }

    function computeDisplayItems() {
        if (search?.trim()) {
            // The open folder is the subtree root, so it would otherwise match itself and list inside itself.
            if (id) return findInSubtree(id, search).filter((node) => node.id !== id)
            const seen = new Set()
            return getRootIds()
                .flatMap((rootId) => findInSubtree(rootId, search))
                .filter((node) => !seen.has(node.id) && seen.add(node.id))
        }
        const items = id ? getChildren(id) : getRootIds().map(getNode).filter(Boolean)
        return items
    }

    function handleAddNewOpen(event) {
        setAddNewAnchor(event.currentTarget)
    }

    function handleAddNewClose() {
        setAddNewAnchor(null)
    }

    async function handleAddFolder() {
        handleAddNewClose()
        const created = await openAddFolderDialog({ onSubmit: submitNewFolder })
        if (!created) return
        library.setFolder(created)
        successSnackbar("Folder created")
    }

    // A duplicate name at this level throws from the resolver and surfaces in the dialog.
    async function submitNewFolder(value) {
        return createAuthoringLegislationGroup({ name: value.name, parentId: id ?? null })
    }

    async function handleAddLegislation() {
        handleAddNewClose()
        const created = await openAddLegislationDialog({ onSubmit: submitNewLegislation })
        if (!created) return
        library.placeLegislation(created)
        successSnackbar("Legislation created")
    }

    // Runs on the dialog's OK while it stays open
    async function submitNewLegislation(value) {
        if (!(await confirmDuplicateDestination(value.url))) {
            const cancelled = new Error("Duplicate destination cancelled")
            cancelled.silent = true
            throw cancelled
        }
        return createAuthoringLegislation({
            title: value.title,
            url: value.url || null,
            description: value.description || null,
            parentId: id,
        })
    }
}
